import { useCallback, useState } from 'react';

import { useRouter as useAppRouter } from 'next/navigation';

import { ISanitizedCompany } from 'types/company';

import { createStory } from 'services/api/firestore/client';

import { useUser } from 'hooks/auth/user';
import { useTrackStoryCreation } from 'hooks/tracking';

export const useCreateStory = (
  company: ISanitizedCompany,
  collectionId: string = '',
) => {
  const { trackStoryCreation } = useTrackStoryCreation();
  const { push } = useAppRouter();
  const { user } = useUser();

  const [isLoading, setIsLoading] = useState(false);

  const createNewStory = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    const { storyId } = await createStory({
      userId: user.uid,
      company,
      collectionId,
    });

    trackStoryCreation({
      storyId: storyId,
      createdAt: new Date().toISOString(),
    });

    // Unlike `next/router`'s `push`, App Router's `push` returns void rather than a
    // promise that resolves once navigation completes, so there is nothing to await
    // here. Leave `isLoading` true instead of resetting it: the button stays disabled
    // with its spinner up until this component unmounts on navigation, rather than
    // flashing back to its idle state while the soft navigation is still in flight.
    push(`/${company.slug}/create?draftId=${storyId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    isLoading,
    createNewStory,
  };
};
